import React, { useState, useEffect } from 'react';
import { registerLandlord, requestRegistrationOTP } from '../services/api';

export default function Register({ onRegister, onBack }) {
  const [step, setStep] = useState('details');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [referralLocked, setReferralLocked] = useState(false); // true if code came from URL
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Auto-read referral code from URL — e.g. rentos.co.in/register?ref=RNTS-X7K2
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) {
      setReferralCode(ref.trim().toUpperCase());
      setReferralLocked(true); // lock field so user can't accidentally clear it
    }
  }, []);

  const handleSubmitDetails = async () => {
    if (!name.trim()) {
      setError('Please enter your full name');
      return;
    }
    if (phone.length !== 10) {
      setError('Enter a valid 10-digit phone number');
      return;
    }
    if (!referralCode.trim()) {
      setError('A referral code is required. Please ask an existing RentOS landlord for their code.');
      return;
    }
    setLoading(true); setError('');
    try {
      await requestRegistrationOTP(phone);
      setStep('otp');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send OTP');
    } finally { setLoading(false); }
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) {
      setError('Enter the 6-digit OTP');
      return;
    }
    setLoading(true); setError('');
    try {
      const res = await registerLandlord({
        name: name.trim(),
        phone,
        email: email.trim() || null,
        referral_code: referralCode.trim().toUpperCase(),
        otp
      });
      onRegister(
        {
          name: res.data.landlord_name,
          id: res.data.landlord_id,
          referral_code: res.data.referral_code || ''
        },
        res.data.access_token
      );
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally { setLoading(false); }
  };

  const inputStyle = {
    width: '100%', padding: '12px 14px',
    border: '1.5px solid #e2e8f0', borderRadius: 8,
    fontSize: 14, marginBottom: 16,
    boxSizing: 'border-box', fontFamily: 'inherit'
  };

  const labelStyle = {
    color: '#374151', marginBottom: 6,
    fontWeight: 500, fontSize: 14,
    display: 'block'
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #1a56db 0%, #1e40af 100%)'
    }}>
      <div style={{
        background: 'white', borderRadius: 16,
        padding: 40, width: 360,
        boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <h1 style={{
            fontSize: 28, fontWeight: 700,
            color: '#1a56db', marginBottom: 4
          }}>RentOS</h1>
          <p style={{ color: '#64748b', fontSize: 13 }}>
            Create your landlord account
          </p>
        </div>

        {step === 'details' ? (
          <>
            <label style={labelStyle}>Full Name</label>
            <input
              type="text"
              placeholder="As it should appear on receipts"
              value={name}
              onChange={e => setName(e.target.value)}
              style={inputStyle}
            />

            <label style={labelStyle}>Mobile Number</label>
            <input
              type="tel"
              placeholder="10-digit WhatsApp number"
              value={phone}
              onChange={e => setPhone(
                e.target.value.replace(/\D/g, '').slice(0, 10)
              )}
              style={inputStyle}
            />

            <label style={labelStyle}>
              Email <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span>
            </label>
            <input
              type="email"
              placeholder="For account & receipt records"
              value={email}
              onChange={e => setEmail(e.target.value)}
              style={inputStyle}
            />

            <label style={labelStyle}>Referral Code</label>
            <div style={{ position: 'relative', marginBottom: 16 }}>
              <input
                type="text"
                placeholder="e.g. RNTS-X7K2"
                value={referralCode}
                onChange={e => !referralLocked && setReferralCode(
                  e.target.value.toUpperCase()
                )}
                readOnly={referralLocked}
                style={{
                  ...inputStyle,
                  marginBottom: 0,
                  background: referralLocked ? '#f0fdf4' : 'white',
                  color: referralLocked ? '#166534' : '#1e293b',
                  border: referralLocked
                    ? '1.5px solid #86efac'
                    : '1.5px solid #e2e8f0',
                  letterSpacing: 2,
                  fontWeight: referralLocked ? 600 : 400
                }}
              />
              {referralLocked && (
                <span style={{
                  position: 'absolute', right: 12, top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: 12, color: '#16a34a', fontWeight: 500
                }}>✓ Applied</span>
              )}
            </div>
            {!referralLocked && (
              <p style={{
                fontSize: 12, color: '#94a3b8',
                marginTop: -12, marginBottom: 16
              }}>
                Ask an existing RentOS landlord for their referral code.
              </p>
            )}

            <button
              onClick={handleSubmitDetails}
              disabled={loading}
              style={{
                width: '100%', padding: 13,
                background: loading ? '#93c5fd' : '#1a56db',
                color: 'white', borderRadius: 8,
                fontSize: 15, fontWeight: 600,
                border: 'none', cursor: 'pointer',
                marginBottom: 12
              }}
            >
              {loading ? 'Sending OTP...' : 'Continue'}
            </button>

            <div style={{ textAlign: 'center' }}>
              <button
                onClick={onBack}
                style={{
                  background: 'none', border: 'none',
                  color: '#64748b', fontSize: 13, cursor: 'pointer'
                }}
              >
                Already have an account? Login
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={{
              background: '#f0fdf4', borderRadius: 8,
              padding: '12px 16px', marginBottom: 20,
              border: '1px solid #bbf7d0'
            }}>
              <p style={{ color: '#166534', fontSize: 13, fontWeight: 500 }}>
                OTP sent to your WhatsApp
              </p>
              <p style={{ color: '#166534', fontSize: 12, marginTop: 2 }}>
                OTP sent to +91 {phone} on WhatsApp
              </p>
            </div>

            <label style={labelStyle}>Enter OTP to verify</label>
            <input
              type="number"
              placeholder="6-digit OTP"
              value={otp}
              onChange={e => setOtp(e.target.value.slice(0, 6))}
              onKeyDown={e => e.key === 'Enter' && handleVerifyOTP()}
              style={{
                width: '100%', padding: '12px 14px',
                border: '1.5px solid #e2e8f0', borderRadius: 8,
                fontSize: 20, letterSpacing: 6,
                textAlign: 'center', marginBottom: 16,
                boxSizing: 'border-box'
              }}
            />
            <button
              onClick={handleVerifyOTP}
              disabled={loading}
              style={{
                width: '100%', padding: 13,
                background: loading ? '#93c5fd' : '#1a56db',
                color: 'white', borderRadius: 8,
                fontSize: 15, fontWeight: 600,
                border: 'none', cursor: 'pointer'
              }}
            >
              {loading ? 'Verifying...' : 'Verify & Enter RentOS'}
            </button>
          </>
        )}

        {error && (
          <p style={{
            color: '#dc2626', fontSize: 13,
            marginTop: 12, textAlign: 'center'
          }}>
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
